import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#FAFAF9] overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-20 left-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Quote className="text-[#D4AF37] mx-auto mb-6" size={40} />
          <blockquote className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#1C1917] italic max-w-4xl mx-auto leading-relaxed">
            Accomplis chaque acte de ta vie comme s'il devait être le dernier.
          </blockquote>
          <p className="mt-4 text-[#D4AF37] uppercase tracking-widest text-sm">
            — Marc Aurèle
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Images Grid - Bien cadrees */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                {/* Photo 1 - Haut gauche - visage visible */}
                <div className="overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src="https://gillesdelieuze.com/wp-content/uploads/2024/03/IMG_9881_www.vincentphotographie.com_-683x1024.jpg"
                    alt="Gilles Delieuze"
                    className="w-full h-48 md:h-64 object-cover object-[center_20%]"
                  />
                </div>
                {/* Photo 3 - Bas gauche: portrait face visible */}
                <div className="overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src="https://gillesdelieuze.com/wp-content/uploads/2024/03/IMG_9977_www.vincentphotographie.com_-1024x683.jpg"
                    alt="Gilles Delieuze"
                    className="w-full h-64 md:h-80 object-cover object-[center_30%]"
                  />
                </div>
              </div>
              <div className="pt-8">
                {/* Photo droite: cheveux visibles en haut, genoux en bas */}
                <div className="overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src="https://gillesdelieuze.com/wp-content/uploads/2024/03/IMG_0013_www.vincentphotographie.com_-650x650.jpg"
                    alt="Gilles Delieuze"
                    className="w-full h-72 md:h-96 object-cover object-[center_10%]"
                  />
                </div>
              </div>
            </div>
            {/* Gold Accent */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-[#D4AF37]/30 rounded-2xl -z-10" />
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-4">
              À propos
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight mb-6">
              Qui est <span className="italic text-[#D4AF37]">Gilles</span> ?
            </h2>

            <div className="space-y-6 text-[#44403C] text-lg leading-relaxed">
              <p>
                À chacune de mes rencontres avec vous, je me rends compte à quel point
                l'humain est beau une fois qu'il ose se mettre à nu. Oser enlever toutes
                les couches, nos masques que nous avons construits est souvent le chemin
                d'une Vie.
              </p>
              <p>
                Il n'y a jamais à devenir, il nous est simplement permis d'accueillir ce
                qui se vit déjà, ce qui Est. Je n'ai de cesse que de vous accompagner à
                vous déployer dans votre nature la plus essentielle et véritable.
              </p>
              <p className="font-medium text-[#1C1917]">
                Ma vocation : transformer le parcours semé de doutes en une marche assurée
                dans la clarté et la foi.
              </p>
            </div>

            <a
              href="https://gillesdelieuze.com/a-propos/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-link"
              className="inline-block mt-8 border border-[#D4AF37] text-[#D4AF37] rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
            >
              En savoir plus
            </a>
          </motion.div>
        </div>

        {/* Stats - Simplified */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-20 border-t border-[#D4AF37]/20"
        >
          <div className="text-center" data-testid="stat-0">
            <p className="font-display text-4xl md:text-5xl text-[#D4AF37] mb-2">26</p>
            <p className="text-[#44403C] text-sm uppercase tracking-wider">ans d'expérience</p>
          </div>
          <div className="text-center" data-testid="stat-1">
            <p className="font-display text-4xl md:text-5xl text-[#D4AF37] mb-2">25K+</p>
            <p className="text-[#44403C] text-sm uppercase tracking-wider">clients</p>
          </div>
          <div className="text-center" data-testid="stat-2">
            <p className="font-display text-4xl md:text-5xl text-[#D4AF37] mb-2">1M+</p>
            <p className="text-[#44403C] text-sm uppercase tracking-wider">vues YouTube</p>
          </div>
          <div className="text-center" data-testid="stat-3">
            <p className="font-display text-4xl md:text-5xl text-[#D4AF37] mb-2">12K+</p>
            <p className="text-[#44403C] text-sm uppercase tracking-wider">membres tribu</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
